import { getEnumGvntTypeValues } from '../autogen/bundle.js';
import { getEnumGvntTypeName   } from '../autogen/bundle.js';
import { getEnumGvntStatusName } from '../autogen/bundle.js';

/**
 * @typedef { import('./event.js').PlayerGroupId } PlayerGroupId
 */
import { isPlayerGroupQA } from './event.js';

/**
 * @typedef { import('../util.js').Role } Role
 */
import { getStrWithLowFirstLetter } from '../util.js';
import { getOptionalTextMomentStr } from '../util.js';
import { getDurationStr           } from '../util.js';
import { getDurationStrSimple     } from '../util.js';
import { isNumberSpecified        } from '../util.js';
import { isRoleAdmin              } from '../util.js';

/**
 * @typedef {Object} GvntPreview
 * @property {number}        id
 * @property {number}        type
 * @property {PlayerGroupId} playerGroupId
 * @property {GvntStatus}    status
 * @property {number}        stime
 * @property {number}        srtime
 * @property {number}        ftime
 * @property {number}        frtime
 * @property {boolean}       startsOnlyOnce
 */

/**
 * @typedef {Object} Gvnt
 * @property {number} id
 * @property {number} type
 * @property {GvntStatus} status
 * @property {number} ctime
 * @property {number} stime
 * @property {number} srtime
 * @property {number} ftime
 * @property {number} frtime
 * @property {number} client_min_version
 * @property {number} for_qa
 * @property {number} starts_only_once
 */

export const GVNT_TYPE_NONE              = 0;
export const GVNT_TYPE_FLOWER_FEST       = 1;
export const GVNT_TYPE_HALLOWEEN         = 2;
export const GVNT_TYPE_SLAGGERS_BIRTHDAY = 3;
export const GVNT_TYPE_CHRISTMAS         = 4;
export const GVNT_TYPE_CHINESE_NY_21     = 5;

export function getGnvtTypes() {
  const gvntTypes = [];

  for(const t of getEnumGvntTypeValues()) {
    if(t !== GVNT_TYPE_NONE) {
      gvntTypes.push(t);
    }
  }

  return gvntTypes;
}

/**
 * @param {number} code
 * @returns {string}
 */
export function getGvntTypeName(code) {
  switch(code) {
    case GVNT_TYPE_FLOWER_FEST:       return 'Flower fest';
    case GVNT_TYPE_HALLOWEEN:         return 'Halloween';
    case GVNT_TYPE_SLAGGERS_BIRTHDAY: return 'Slaggers birthday';
    case GVNT_TYPE_CHINESE_NY_21:     return 'Chinese New Year 2021';
  }

  return getEnumGvntTypeName(code);
}

// options for component b-form-select
export function getGvntTypeOptions() {
  const options = [];

  const typeList = getGnvtTypes();
  for(const t of typeList) {
    const option = {
      value: t,
      text: getGvntTypeName(t),
    };
    options.push(option);
  }

  return options;
}

/**
 * @typedef {number} GvntStatus
 */

export const GVNT_STATUS_INCOMING = 1;
export const GVNT_STATUS_ACTIVE   = 2;
export const GVNT_STATUS_FINISHED = 3;
export const GVNT_STATUS_CLOSED   = 4;

/**
 * @param {GvntStatus} status
 * @returns {boolean}
 */
export function isGvntStatusIncoming(status) {
  return status === GVNT_STATUS_INCOMING;
}

/**
 * @param {GvntStatus} status
 * @returns {boolean}
 */
export function isGvntStatusActive(status) {
  return status === GVNT_STATUS_ACTIVE;
}

/**
 * @param {GvntStatus} status
 * @returns {boolean}
 */
export function isGvntStatusFinished(status) {
  return status === GVNT_STATUS_FINISHED;
}

/**
 * @param {GvntStatus} status
 * @returns {boolean}
 */
export function isGvntStatusClosed(status) {
  return status === GVNT_STATUS_CLOSED;
}

/**
 * @param {GvntStatus} status
 * @returns {string}
 */
export function getGvntStatusName(status) {
  const name = getEnumGvntStatusName(status);
  return getStrWithLowFirstLetter(name);
}

/**
 * @param {GvntStatus} status
 * @returns {Object}
 */
export function getGvntStatusClass(status) {
  return {
    'active-gvnt': isGvntStatusActive(status)
  };
}

/**
 * @param {Role} role
 * @param {GvntStatus} gvntStatus
 * @returns {boolean}
 */
export function canEditGvnt(role, gvntStatus) {
  if(!mayManageGvnt(role)) {
    return false;
  }
  return isGvntEditable(gvntStatus);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayManageGvnt(role) {
  return isRoleAdmin(role);
}

/**
 * @param {GvntStatus} status
 * @returns {boolean}
 */
export function isGvntEditable(status) {
  switch(status) {
    case GVNT_STATUS_INCOMING:
      return true;
    case GVNT_STATUS_ACTIVE:
      return true;
    default:
      return false;
  }
}

/**
 * @param {GvntStatus} status
 * @returns {boolean}
 */
export function isGvntFtimeEditable(status) {
  return isGvntStatusIncoming(status) || isGvntStatusActive(status);
}

/**
 * @param {Object} gvnt
 * @returns {boolean}
 */
export function canRunGvnForAll(gvnt) {
  if(!isGvntEditable(gvnt.status)) {
    return false;
  }

  return isPlayerGroupQA(gvnt.playerGroupId);
}

/**
 * @param {boolean} startsOnlyOnce
 * @returns {string}
 */
export function getGvntStartsOnlyOnceName(startsOnlyOnce) {
  return startsOnlyOnce ? 'true' : 'false';
}

/**
 * @param {Object} gvnt
 * @returns {Object | null}
 */
export function getGvntStartsOnlyOnceClass(gvnt) {
  if(isGvntStatusFinished(gvnt.status)) {
    return null;
  }

  return {
    'active-gvnt': gvnt.startsOnlyOnce
  };
}

/**
 * @param {number} time
 * @returns {string}
 */
export function getGvntRealStartTimeStr(time) {
  return getOptionalTextMomentStr(time, '---'); // не началось
}

/**
 * @param {number} time
 * @returns {string}
 */
export function getGvntRealFinishTimeStr(time) {
  return getOptionalTextMomentStr(time, '---'); // не завершено
}

/**
 * @param {Gvnt} gvnt
 * @param {number} nowSec
 * @returns {string}
 */
export function getGvntRemainingTimeStr(gvnt, nowSec) {
  if(gvnt.status === GVNT_STATUS_INCOMING) {
    return '---'; // не началось
  }

  if(gvnt.status === GVNT_STATUS_FINISHED) {
    return '---'; // завершено
  }

  const timeEnded = nowSec >= gvnt.ftime;
  if(timeEnded) {
    return 'time ended';
  }

  const remainintTime = gvnt.ftime - nowSec;
  return getDurationStrSimple(remainintTime);
}

//-----------------------------------------------------------------------------

/**
 * @param {Gvnt} gvnt
 * @returns {number}
 */
function getGvntPlannedDuration(gvnt) {
  return gvnt.ftime - gvnt.stime;
}

/**
 * @param {Gvnt} gvnt 
 * @returns {number}
 */
export function getGvntRealDuration(gvnt) {
  return gvnt.frtime - gvnt.srtime;
}

//-----------------------------------------------------------------------------

/**
 * @param {Gvnt} gvnt 
 * @returns {string}
 */
export function getGvntPlannedDurationStrSimple(gvnt) {
  const plannedDuration = getGvntPlannedDuration(gvnt);
  return getDurationStrSimple(plannedDuration);
}

/**
 * @param {Gvnt} gvnt 
 * @returns {string}
 */
export function getGvntPlannedDurationStr(gvnt) {
  const plannedDuration = getGvntPlannedDuration(gvnt);
  return getDurationStr(plannedDuration);
}

/**
 * @param {Gvnt} gvnt 
 * @returns {string}
 */
export function getGvntRealDurationStr(gvnt) {
  if(!isNumberSpecified(gvnt.srtime)) {
    return 'not started';
  }
  if(!isNumberSpecified(gvnt.frtime)) {
    return 'not finished';
  }
  const realDuration = getGvntRealDuration(gvnt);
  return getDurationStr(realDuration);
}

/**
 * @param {Gvnt} gvnt 
 * @returns {string}
 */
export function getGvntRealDurationStrSimple(gvnt) {
  if(!isNumberSpecified(gvnt.srtime)) {
    return 'not started';
  }
  if(!isNumberSpecified(gvnt.frtime)) {
    return 'not finished';
  }
  const realDuration = getGvntRealDuration(gvnt);
  return getDurationStrSimple(realDuration);
}

//-----------------------------------------------------------------------------

export function isPlayerGvntActive(ftime) {
  return ftime === 0;
}
